import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { ImCheckboxChecked } from "react-icons/im";
import TicketModal from "./ticket";

function AuditorForm({ master, setLoanding, loanding }) {
  const [checkedItems, setCheckedItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [audited, setAudited] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showTicket, setShowTicket] = useState(false);
  const [ticketData, setTicketData] = useState({});
  const [tolerance, setTolerance] = useState(null);
  const hasAutoAuditRun = useRef(false);

  const housesSource = useMemo(() => {
    if (!master) return [];
    return master.allHouses?.length > 0
      ? master.allHouses
      : master.allMasters || [];
  }, [master]);

  function noCommas(value) {
    return String(value || "").replace(/,/g, "");
  }

  const handleAudit = useCallback(
    async (silently = false) => {
      setLoanding(true);

      if (selectedIds.length === 0) {
        if (!silently) setShowAlert(true);
        setLoanding(false);
        return;
      }

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_URL_BACKEND_EXPO}/api/v1/exp_auditor/make_audit`,
          {
            auditors: selectedIds,
            master_id: master?.resp?.id,
            client_autorization: null,
          },
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          }
        );

        if (res.data.code === 1) {
          setAudited(true);
          setLoanding(false);
          setTolerance(res.data.data.status);
          let vol = parseFloat(noCommas(res.data.data.cubiscan_vol));
          vol = (vol / 1000).toFixed(4);
          setTicketData({
            awb: master?.resp?.awb,
            pieces: master?.resp?.pieces,
            weight: master?.resp?.weight,
            chargeable_weight: master?.resp?.chargeable_weight,
            cubeVol: vol,
            cubGrossWt: res.data.data.cubiscan_wgt,
            cubChWt: res.data.data.cubiscan_chwt,
            tolerance: res.data.data.tolerance,
          });

          if (!silently) {
            Swal.fire({
              title: res.data.message || "Auditoría calculada correctamente",
              icon: "success",
              customClass: {
                confirmButton: "btn-ok",
                title: "alert-title",
                popup: "alert-bg",
              },
            });
          }
        }
      } catch (error) {
        setLoanding(false);
        console.error("Error en handleAudit:", error);
        if (!silently) {
          Swal.fire({
            title:
              error.response?.data?.message ||
              "Error Inesperado al realizar la auditoría",
            icon: "error",
            customClass: {
              confirmButton: "btn-ok",
              title: "alert-title",
              popup: "alert-bg",
            },
          });
        }
      }
    },
    [
      selectedIds,
      master?.resp?.id,
      master?.resp?.awb,
      master?.resp?.pieces,
      master?.resp?.weight,
      master?.resp?.chargeable_weight,
      setLoanding,
      setAudited,
      setTicketData,
      setShowAlert,
      setTolerance,
    ]
  );

  const handleSelectAll = () => {
    const newState = !selectAll;
    setSelectAll(newState);

    const updated = checkedItems.map(() => newState);
    setCheckedItems(updated);

    if (newState) {
      setSelectedIds(housesSource.map((h) => `${h.id},${h.type}`));
    } else {
      setSelectedIds([]);
    }

    setShowAlert(false);
  };

  const handleCheckboxChange = (index, id) => {
    setCheckedItems((prev) => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });

    setSelectedIds((prev) => {
      const set = new Set(prev);
      if (checkedItems[index]) {
        set.delete(id);
      } else {
        set.add(id);
      }
      const newSet = Array.from(set);
      // Mostrar alerta si no quedan items seleccionados
      if (newSet.length === 0) {
        setShowAlert(true);
      } else {
        setShowAlert(false);
      }
      return newSet;
    });
  };

  function formatDate(input) {
    try {
      const date = new Date(input);

      if (isNaN(date.getTime())) {
        return null; // fecha inválida
      }

      const pad = (n) => String(n).padStart(2, "0");

      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (err) {
      console.error("Error formateando fecha:", err);
      return null;
    }
  }

  useEffect(() => {
    if (!master || !housesSource) return;

    const initialChecks = housesSource.map((h) => h.is_general_checked === 1);
    setCheckedItems(initialChecks);

    const initialIds = housesSource
      .filter((h) => h.is_general_checked === 1)
      .map((h) => `${h.id},${h.type}`);

    setSelectedIds(initialIds);
    setAudited(!!master.ticket);
    setTolerance(master.tolerance);
    setSelectAll(false);
    setShowAlert(initialIds.length === 0);
  }, [master, housesSource]);

  useEffect(() => {
    if (!hasAutoAuditRun.current && master?.ticket && selectedIds.length > 0) {
      console.log("🚀 Disparando auditoría automática (una sola vez)...");
      handleAudit(true);
      hasAutoAuditRun.current = true; // ✅ marcar como ejecutado
    }
  }, [master?.ticket, selectedIds.length, handleAudit]);

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-3">
            <p>
              <span className="fw-bold">Master: </span> {master?.resp?.awb}{" "}
            </p>
          </div>
          <div className="col-3">
            <p>
              <span className="fw-bold">Origen: </span> {master?.resp?.orig}{" "}
            </p>
          </div>
          <div className="col-3">
            <p>
              <span className="fw-bold">Destino: </span> {master?.resp?.dest}{" "}
            </p>
          </div>
          <div className="col-3">
            <p>
              <span className="fw-bold">Piezas: </span> {master?.resp?.pieces}{" "}
            </p>
          </div>
        </div>

        <div className="row">
          <div className="col-3">
            <p>
              <span className="fw-bold">Peso: </span> {master?.resp?.weight}{" "}
            </p>
          </div>
          <div className="col-3">
            <p>
              <span className="fw-bold">Volúmen: </span> {master?.resp?.volume}{" "}
            </p>
          </div>
          <div className="col-3">
            <p>
              <span className="fw-bold">Tipo de mercancía: </span>{" "}
              {master?.resp?.merch}{" "}
            </p>
          </div>
        </div>

        <div className="row">
          <div className="col mt-4 d-flex justify-content-end">
            <button className="btnn" onClick={handleSelectAll}>
              {" "}
              <ImCheckboxChecked /> Todos
            </button>
          </div>
        </div>

        <table className="table table-striped mt-3">
          <thead className="text-center">
            <tr>
              {master?.allHouses?.length > 0 && (
                <th style={{ backgroundColor: "#020C41", color: "white" }}>
                  HBS
                </th>
              )}
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Fecha
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Nombre Archivo
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Volúmen
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Peso
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Tarima
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="text-center">
            {housesSource?.length > 0
              ? housesSource.map((data, _index) => (
                  <tr key={_index}>
                    {master?.allHouses?.length > 0 && <td>{data.hbs}</td>}
                    <td>{formatDate(data.created_at)}</td>
                    <td>{data.file_name}</td>
                    <td>
                      {data.medition_type === 1
                        ? data.cubevol_gross / 1000
                        : data.realvol_net / 1000}
                    </td>
                    <td>
                      {data.medition_type === 1
                        ? data.weight_net
                        : data.weight_gross}
                    </td>
                    <td>
                      {data.medition_type === 1 ? "Cubicada" : "Delineada"}
                    </td>
                    <td>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={checkedItems[_index] || false}
                        onChange={() =>
                          handleCheckboxChange(
                            _index,
                            `${data.id},${data.type}`
                          )
                        }
                      />
                    </td>
                  </tr>
                ))
              : ""}
          </tbody>
        </table>

        {showAlert && (
          <div className="alert alert-danger mt-3" role="alert">
            Debes seleccionar al menos una medición
          </div>
        )}

        {tolerance == 0 && (
          <div className="alert alert-success mb-3" role="alert">
            Valores aceptados, se cobra conforme los datos de la guía.
          </div>
        )}

        {tolerance == 1 && (
          <div className="alert alert-danger mb-3" role="alert">
            Medición fuera de rango:
            <br />
            <br />
            Valores excedidos, se requiere cambio de documentos
          </div>
        )}

        {tolerance == -1 && (
          <div className="alert alert-danger mb-3" role="alert">
            Medición fuera de rango:
            <br />
            <br />
            Acepto que se cobre conforme a los datos asentados en la guía
          </div>
        )}

        <div className="row">
          <div className="col mt-4 d-flex justify-content-end gap-3">
            {!loanding && (
              <button className="btnn" onClick={handleAudit}>
                Auditar
              </button>
            )}

            {audited && (
              <button
                className="btnn"
                style={showTicket ? { display: "none" } : {}}
                onClick={() => setShowTicket(true)}
              >
                Ticket
              </button>
            )}
          </div>
        </div>
      </div>

      <TicketModal
        show={showTicket}
        onHide={() => setShowTicket(false)}
        data={ticketData}
      />
    </>
  );
}

export default AuditorForm;
